import type { NervousSystem, RegionId, Substance } from '../pharmacology/types';
import { RECEPTOR_TO_REGIONS, type ReceptorId } from '../pharmacology/receptors';
import { REGIONS } from '../pharmacology/regions';
import { computeBrainConcentrationNM } from './pkModel';
import { hillLangmuirOccupancy } from './occupancy';

export interface DoseResponseResult {
  /** Brain concentration in nanomolar at the given dose/time. */
  brainConcentrationNM: number;
  /** Fractional receptor occupancy (0-1) keyed by receptor id. */
  receptorOccupancy: Partial<Record<ReceptorId, number>>;
  /** Fractional activity level (0-1) keyed by anatomical region id. */
  regionActivity: Partial<Record<RegionId, number>>;
  /** Aggregate engagement (0-1) of each nervous-system subdivision. */
  systemActivity: Record<NervousSystem, number>;
  /** Highest occupancy reached at any receptor — the overall "how much is happening" signal. */
  peakOccupancy: number;
}

export const IDLE_SYSTEM_ACTIVITY: Record<NervousSystem, number> = {
  central: 0,
  somatic: 0,
  autonomic: 0,
  enteric: 0,
};

/**
 * Computes receptor occupancy and the derived regional and system-level
 * activity for a substance at a given dose and elapsed time.
 *
 * Region activity is the MAXIMUM occupancy across the receptors mapping to that
 * region, rather than a sum — combining multiple receptor systems additively
 * would overstate regional effects and is not generally how they interact.
 *
 * System activity is likewise the maximum across that subdivision's regions, so
 * a drug acting on one peripheral structure lights the peripheral nervous
 * system without being diluted by the structures it does not touch.
 */
export function computeDoseResponse(
  substance: Substance,
  doseOrReleaseLevel: number,
  tHours: number,
): DoseResponseResult {
  const brainConcentrationNM = computeBrainConcentrationNM(substance.pk, doseOrReleaseLevel, tHours);

  const receptorOccupancy: Partial<Record<ReceptorId, number>> = {};
  for (const binding of substance.bindings) {
    const occupancy = hillLangmuirOccupancy(brainConcentrationNM, binding.kiNM);
    const receptorId = binding.receptor as ReceptorId;
    receptorOccupancy[receptorId] = Math.max(receptorOccupancy[receptorId] ?? 0, occupancy);
  }

  const regionActivity: Partial<Record<RegionId, number>> = {};
  for (const [receptorId, occupancy] of Object.entries(receptorOccupancy) as [ReceptorId, number][]) {
    for (const regionId of RECEPTOR_TO_REGIONS[receptorId] ?? []) {
      regionActivity[regionId] = Math.max(regionActivity[regionId] ?? 0, occupancy);
    }
  }

  const systemActivity: Record<NervousSystem, number> = { ...IDLE_SYSTEM_ACTIVITY };
  for (const [regionId, activity] of Object.entries(regionActivity) as [RegionId, number][]) {
    const system = REGIONS[regionId].system;
    systemActivity[system] = Math.max(systemActivity[system], activity);
  }

  const occupancies = Object.values(receptorOccupancy);
  const peakOccupancy = occupancies.length ? Math.max(...occupancies) : 0;

  return { brainConcentrationNM, receptorOccupancy, regionActivity, systemActivity, peakOccupancy };
}
